import type {
  PopulationFilter,
  QuickStudyAudienceSelection,
} from '@digital-twins/shared-types'

import {
  segmentChipsFromFilter,
  segmentLabelFromChips,
} from './ThreadRunPane/runSegmentFilter'

/**
 * The audience filter a Quick Study card shows, in plain words: the filter's
 * saved segment label when it names the filter (it carries the attribute), else
 * its conditions through the same chip helpers Studies use. Empty when the filter
 * has no conditions: the study ran on the whole audience, whatever the label says. Generic over any
 * filter: it names values, never a template's fields.
 */
export function quickStudyAudienceFilterText(
  audience: QuickStudyAudienceSelection | null | undefined,
): string {
  if (!audience) {
    return ''
  }
  const filter = audience.populationFilter as unknown as PopulationFilter | null | undefined
  const fromFilter = segmentLabelFromChips(segmentChipsFromFilter(filter))
  const hasConditions = Array.isArray(filter?.conditions) && filter.conditions.length > 0
  if (!hasConditions) {
    return ''
  }
  return audience.segmentLabel?.trim() || fromFilter
}

const FILTER_ERROR_KEYS = {
  quick_study_audience_filter_invalid: 'threads.quickStudy.filterInvalid',
  quick_study_audience_filter_no_match: 'threads.quickStudy.filterNoMatch',
  quick_study_audience_filter_too_small: 'threads.quickStudy.filterTooSmall',
} as const

/** The i18n key for a coded audience-filter refusal, or null for any other failure. */
export function quickStudyFilterErrorKey(
  failure: unknown,
): (typeof FILTER_ERROR_KEYS)[keyof typeof FILTER_ERROR_KEYS] | null {
  const code = (failure as { response?: { data?: { code?: string } } })?.response
    ?.data?.code
  if (code && Object.prototype.hasOwnProperty.call(FILTER_ERROR_KEYS, code)) {
    return FILTER_ERROR_KEYS[code as keyof typeof FILTER_ERROR_KEYS]
  }
  return null
}
